import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon, PencilSquareIcon, TrashIcon, MagnifyingGlassIcon,
  ArrowDownTrayIcon, ArrowUpTrayIcon, DocumentTextIcon,
  ChevronDownIcon, ChevronUpIcon, XMarkIcon, UserPlusIcon,
  ArrowsRightLeftIcon, CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { empresasApi } from '../../api/empresas';

const REGIMEN_OPTS = [
  { value: '', label: 'Sin especificar' },
  { value: 'general', label: 'Régimen General' },
  { value: 'simplificado', label: 'Régimen Simplificado' },
  { value: 'especial', label: 'Régimen Especial' },
  { value: 'agricola', label: 'Régimen Agrícola' },
  { value: 'turismo', label: 'Régimen de Turismo' },
];

const EMPTY_SOCIO = { nombre: '', cedula: '', porcentaje: '' };

const EMPTY_FORM = {
  nombre: '', rnc: '', actividad_economica: '', regimen_fiscal: '',
  telefono: '', email: '', direccion: '',
  fecha_constitucion: '', representante_legal: '',
  socios: [],
  costo: '', cuenta_bancaria: false, rpe: false,
  deudas_tributarias: false, saldo_favor_itbis: false, saldo_favor_ir2: false,
  descripcion: '', activa: true, en_carusel: false, es_favorita: false,
};

const Toggle = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2 cursor-pointer select-none">
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${checked ? 'bg-[#12284C]' : 'bg-gray-300'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
    <span className="text-sm text-gray-700">{label}</span>
  </label>
);

const SectionHeader = ({ title, expanded, onToggle, accent }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`flex items-center justify-between w-full px-3 py-2 rounded-lg transition-colors ${
      accent
        ? 'bg-indigo-50 hover:bg-indigo-100'
        : 'bg-gray-100 hover:bg-gray-200'
    }`}
  >
    <span className={`text-sm font-semibold ${accent ? 'text-[#12284C]' : 'text-gray-700'}`}>
      {title}
    </span>
    {expanded
      ? <ChevronUpIcon className="h-4 w-4 text-[#12284C]" />
      : <ChevronDownIcon className="h-4 w-4 text-[#12284C]" />}
  </button>
);

export default function GestionEmpresasAdmin() {
  const navigate = useNavigate();
  const importRef = useRef(null);

  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroActiva, setFiltroActiva] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [sections, setSections] = useState({
    identificacion: true, contacto: true, corporativo: false,
    financiero: false, estado: false,
  });

  const toggleSection = (s) => setSections((p) => ({ ...p, [s]: !p[s] }));

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filtroActiva !== '') params.activa = filtroActiva;
      const data = await empresasApi.listar(params);
      setEmpresas(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, filtroActiva]);

  useEffect(() => {
    const t = setTimeout(cargar, search ? 400 : 0);
    return () => clearTimeout(t);
  }, [cargar]);

  const abrirNuevo = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setShowForm(true);
  };

  const abrirEditar = (emp) => {
    setEditing(emp.id);
    setForm({
      nombre: emp.nombre || '',
      rnc: emp.rnc || '',
      actividad_economica: emp.actividad_economica || '',
      regimen_fiscal: emp.regimen_fiscal || '',
      telefono: emp.telefono || '',
      email: emp.email || '',
      direccion: emp.direccion || '',
      fecha_constitucion: emp.fecha_constitucion || '',
      representante_legal: emp.representante_legal || '',
      socios: Array.isArray(emp.socios) ? emp.socios : [],
      costo: emp.costo ?? '',
      cuenta_bancaria: !!emp.cuenta_bancaria,
      rpe: !!emp.rpe,
      deudas_tributarias: !!emp.deudas_tributarias,
      saldo_favor_itbis: !!emp.saldo_favor_itbis,
      saldo_favor_ir2: !!emp.saldo_favor_ir2,
      descripcion: emp.descripcion || '',
      activa: emp.activa !== false,
      en_carusel: !!emp.en_carusel,
      es_favorita: !!emp.es_favorita,
    });
    setErrors({});
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.nombre.trim()) errs.nombre = 'El nombre es requerido';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        rnc: form.rnc || null,
        costo: form.costo === '' ? 0 : parseFloat(form.costo),
        socios: form.socios.filter((s) => s.nombre.trim()),
      };
      if (editing) {
        await empresasApi.actualizar(editing, payload);
      } else {
        await empresasApi.crear(payload);
      }
      setShowForm(false);
      cargar();
    } catch (err) {
      const data = err?.response?.data || {};
      const mapped = {};
      Object.keys(data).forEach((k) => {
        mapped[k] = Array.isArray(data[k]) ? data[k].join(', ') : String(data[k]);
      });
      setErrors(Object.keys(mapped).length ? mapped : { nombre: 'Error al guardar' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (emp) => {
    if (!window.confirm(`¿Eliminar "${emp.nombre}"?`)) return;
    try {
      await empresasApi.eliminar(emp.id);
      cargar();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const res = await empresasApi.importarExcel(file);
      setImportResult(res);
      cargar();
    } catch (err) {
      setImportResult({ error: err?.response?.data?.error || 'Error al importar' });
    } finally {
      setImportLoading(false);
      e.target.value = '';
    }
  };

  const handleSincronizarDGII = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const res = await empresasApi.sincronizarDGII();
      setSyncResult(res);
      cargar();
    } catch (err) {
      setSyncResult({ error: err?.response?.data?.detail || 'Error al sincronizar' });
    } finally {
      setSyncLoading(false);
    }
  };

  // Socios helpers
  const addSocio = () => setForm((p) => ({ ...p, socios: [...p.socios, { ...EMPTY_SOCIO }] }));
  const updateSocio = (i, field, val) =>
    setForm((p) => {
      const s = [...p.socios];
      s[i] = { ...s[i], [field]: val };
      return { ...p, socios: s };
    });
  const removeSocio = (i) =>
    setForm((p) => ({ ...p, socios: p.socios.filter((_, idx) => idx !== i) }));

  return (
    <div className="px-4 sm:px-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Gestión de Empresas</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Base de datos unificada de empresas clientes
        </p>
      </div>

      {/* Actions Bar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            className="pl-9 pr-3 py-2 w-full border border-gray-300 rounded-lg bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#12284C]"
            placeholder="Buscar por nombre, RNC, actividad..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#12284C]"
          value={filtroActiva}
          onChange={(e) => setFiltroActiva(e.target.value)}
        >
          <option value="">Todas</option>
          <option value="true">Activas</option>
          <option value="false">Inactivas</option>
        </select>

        <button
          onClick={() => empresasApi.descargarPlantilla()}
          className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50"
          title="Descargar plantilla Excel"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          Plantilla
        </button>

        <button
          onClick={() => importRef.current?.click()}
          disabled={importLoading}
          className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50"
          title="Importar desde Excel"
        >
          <ArrowUpTrayIcon className="h-4 w-4" />
          {importLoading ? 'Importando...' : 'Importar Excel'}
        </button>
        <input ref={importRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />

        <button
          onClick={handleSincronizarDGII}
          disabled={syncLoading}
          className="flex items-center gap-1 px-3 py-2 text-sm border border-indigo-300 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-medium"
          title="Importar empresas desde el módulo Gestión de Empresas (DGII)"
        >
          <ArrowsRightLeftIcon className="h-4 w-4" />
          {syncLoading ? 'Sincronizando...' : 'Sincronizar DGII'}
        </button>

        <button
          onClick={abrirNuevo}
          className="flex items-center gap-1 px-4 py-2 text-sm bg-[#12284C] hover:bg-[#0d1e38] text-white rounded-lg font-medium"
        >
          <PlusIcon className="h-4 w-4" />
          Nueva Empresa
        </button>
      </div>

      {/* Sync Result */}
      {syncResult && (
        <div className={`mb-4 p-3 rounded-lg text-sm flex items-start gap-2 ${syncResult.error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
          <CheckCircleIcon className="h-4 w-4 mt-0.5 shrink-0" />
          <span className="flex-1">
            {syncResult.error
              ? syncResult.error
              : `Sincronización completada: ${syncResult.creadas} empresa(s) nueva(s), ${syncResult.actualizadas} actualizada(s), ${syncResult.omitidas} sin cambios.`}
          </span>
          <button onClick={() => setSyncResult(null)} className="underline text-xs shrink-0">Cerrar</button>
        </div>
      )}

      {/* Import Result */}
      {importResult && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${importResult.error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
          {importResult.error
            ? importResult.error
            : `Importación completada: ${importResult.creadas} creadas, ${importResult.actualizadas} actualizadas${importResult.errores?.length ? `, ${importResult.errores.length} errores` : ''}.`}
          <button onClick={() => setImportResult(null)} className="ml-3 underline text-xs">Cerrar</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Empresa', 'RNC', 'Actividad', 'Contacto', 'Estado', 'Acciones'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Cargando...</td></tr>
              ) : empresas.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No se encontraron empresas</td></tr>
              ) : empresas.map((emp) => (
                <tr key={emp.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{emp.nombre}</div>
                    {emp.representante_legal && (
                      <div className="text-xs text-gray-400">{emp.representante_legal}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{emp.rnc || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 max-w-48 truncate">{emp.actividad_economica || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="text-gray-600">{emp.email || '—'}</div>
                    {emp.telefono && <div className="text-xs text-gray-400">{emp.telefono}</div>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${emp.activa ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {emp.activa ? 'Activa' : 'Inactiva'}
                    </span>
                    {emp.en_carusel && (
                      <span className="ml-1 inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        Carrusel
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => abrirEditar(emp)}
                        className="text-[#12284C] hover:text-[#12284C]"
                        title="Editar"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/admin/documental?empresa_id=${emp.id}`)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Ver documentos"
                      >
                        <DocumentTextIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(emp)}
                        className="text-red-500 hover:text-red-700"
                        title="Eliminar"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto py-8 px-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editing ? 'Editar Empresa' : 'Nueva Empresa'}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="px-6 py-4 space-y-4 max-h-[75vh] overflow-y-auto">
              {/* Identificación */}
              <div className="border border-indigo-200 rounded-lg overflow-hidden">
                <SectionHeader title="Identificación" expanded={sections.identificacion} onToggle={() => toggleSection('identificacion')} accent />
                {sections.identificacion && (
                  <div className="p-4 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Nombre *</label>
                        <input className={`form-input ${errors.nombre ? 'border-red-500' : ''}`} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
                        {errors.nombre && <p className="text-xs text-red-500 mt-0.5">{errors.nombre}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">RNC</label>
                        <input className={`form-input ${errors.rnc ? 'border-red-500' : ''}`} value={form.rnc} onChange={(e) => setForm({ ...form, rnc: e.target.value })} placeholder="101234567" />
                        {errors.rnc && <p className="text-xs text-red-500 mt-0.5">{errors.rnc}</p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Actividad Económica</label>
                        <input className="form-input" value={form.actividad_economica} onChange={(e) => setForm({ ...form, actividad_economica: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Régimen Fiscal</label>
                        <select className="form-input" value={form.regimen_fiscal} onChange={(e) => setForm({ ...form, regimen_fiscal: e.target.value })}>
                          {REGIMEN_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Contacto */}
              <div className="border border-indigo-200 rounded-lg overflow-hidden">
                <SectionHeader title="Contacto" expanded={sections.contacto} onToggle={() => toggleSection('contacto')} accent />
                {sections.contacto && (
                  <div className="p-4 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Teléfono</label>
                        <input className="form-input" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                        <input className="form-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Dirección</label>
                      <textarea className="form-input" rows={2} value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} />
                    </div>
                  </div>
                )}
              </div>

              {/* Datos Corporativos */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <SectionHeader title="Datos Corporativos" expanded={sections.corporativo} onToggle={() => toggleSection('corporativo')} />
                {sections.corporativo && (
                  <div className="p-4 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Fecha de Constitución</label>
                        <input className="form-input" type="date" value={form.fecha_constitucion} onChange={(e) => setForm({ ...form, fecha_constitucion: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Representante Legal</label>
                        <input className="form-input" value={form.representante_legal} onChange={(e) => setForm({ ...form, representante_legal: e.target.value })} />
                      </div>
                    </div>
                    {/* Socios */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-medium text-gray-600">Socios / Accionistas</label>
                        <button type="button" onClick={addSocio} className="flex items-center gap-1 text-xs text-[#12284C] hover:underline">
                          <UserPlusIcon className="h-3.5 w-3.5" /> Añadir socio
                        </button>
                      </div>
                      {form.socios.length === 0 && (
                        <p className="text-xs text-gray-400 italic">Sin socios registrados</p>
                      )}
                      {form.socios.map((s, i) => (
                        <div key={i} className="grid grid-cols-3 gap-2 mb-2 items-center">
                          <input className="form-input text-xs" placeholder="Nombre" value={s.nombre} onChange={(e) => updateSocio(i, 'nombre', e.target.value)} />
                          <input className="form-input text-xs" placeholder="Cédula/RNC" value={s.cedula} onChange={(e) => updateSocio(i, 'cedula', e.target.value)} />
                          <div className="flex items-center gap-1">
                            <input className="form-input text-xs" placeholder="%" value={s.porcentaje} onChange={(e) => updateSocio(i, 'porcentaje', e.target.value)} />
                            <button type="button" onClick={() => removeSocio(i)} className="text-red-400 hover:text-red-600 shrink-0">
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Descripción / Notas</label>
                      <textarea className="form-input" rows={2} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
                    </div>
                  </div>
                )}
              </div>

              {/* Financiero */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <SectionHeader title="Financiero" expanded={sections.financiero} onToggle={() => toggleSection('financiero')} />
                {sections.financiero && (
                  <div className="p-4 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Costo (RD$)</label>
                      <input className="form-input" type="number" step="0.01" value={form.costo} onChange={(e) => setForm({ ...form, costo: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <Toggle checked={form.cuenta_bancaria} onChange={(v) => setForm({ ...form, cuenta_bancaria: v })} label="Cuenta Bancaria" />
                      <Toggle checked={form.rpe} onChange={(v) => setForm({ ...form, rpe: v })} label="RPE" />
                      <Toggle checked={form.deudas_tributarias} onChange={(v) => setForm({ ...form, deudas_tributarias: v })} label="Deudas Tributarias" />
                      <Toggle checked={form.saldo_favor_itbis} onChange={(v) => setForm({ ...form, saldo_favor_itbis: v })} label="Saldo Favor ITBIS" />
                      <Toggle checked={form.saldo_favor_ir2} onChange={(v) => setForm({ ...form, saldo_favor_ir2: v })} label="Saldo Favor IR2" />
                    </div>
                  </div>
                )}
              </div>

              {/* Estado */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <SectionHeader title="Estado y Visibilidad" expanded={sections.estado} onToggle={() => toggleSection('estado')} />
                {sections.estado && (
                  <div className="p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <Toggle checked={form.activa} onChange={(v) => setForm({ ...form, activa: v })} label="Activa" />
                      <Toggle checked={form.en_carusel} onChange={(v) => setForm({ ...form, en_carusel: v })} label="En Carrusel" />
                      <Toggle checked={form.es_favorita} onChange={(v) => setForm({ ...form, es_favorita: v })} label="Favorita" />
                    </div>
                  </div>
                )}
              </div>
            </form>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm bg-[#12284C] hover:bg-[#0d1e38] text-white rounded-lg font-medium"
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
